import React from 'react';
import Portal from '../Portal';
import './detail-v2.css';

interface NotificationPayload {
  type: string;
  title: string;
  body: string;
  ts?: string;
  url?: string;
  [key: string]: any;
}

interface Props {
  notification: NotificationPayload;
  onClose: () => void;
}

// Traduction des types techniques en labels lisibles + configuration d'affichage
const NOTIFICATION_CONFIG: Record<string, { label: string; icon: string; accent: 'ok' | 'accent' | 'warn' | 'bad' }> = {
  // Publications
  'post_published': { label: 'Post parti', icon: '📲', accent: 'ok' },
  'story_published': { label: 'Story partie', icon: '📸', accent: 'ok' },
  'publish_failed': { label: 'Échec de publication', icon: '⚠️', accent: 'bad' },
  'schedule_failed': { label: 'Programmation échouée', icon: '⚠️', accent: 'bad' },
  'validation_reminder': { label: 'Validation attendue', icon: '⏰', accent: 'accent' },
  'account_disconnected': { label: 'Compte déconnecté', icon: '🔓', accent: 'bad' },
  'manual_publish_needed': { label: 'Publication manuelle', icon: '👤', accent: 'warn' },

  // Clients
  'client_validated': { label: 'Client a validé', icon: '✅', accent: 'ok' },
  'client_rejected': { label: 'Client a refusé', icon: '❌', accent: 'bad' },
  'client_commented': { label: 'Client a commenté', icon: '💬', accent: 'accent' },
  'client_rescheduled': { label: 'Post reprogrammé', icon: '🔄', accent: 'accent' },
  'client_caption_edited': { label: 'Description modifiée', icon: '✏️', accent: 'accent' },
  'client_descheduled': { label: 'Post déprogrammé', icon: '🛑', accent: 'bad' },

  // Pubs
  'boost_ended': { label: 'Pub terminée', icon: '📊', accent: 'accent' },
};

const NotificationDetailModal: React.FC<Props> = ({ notification, onClose }) => {
  // Le backend envoie le type dans data.type (notificationService), pas à la racine :
  // sans ce fallback la popup affichait toujours « Notification » au lieu du libellé lisible.
  const notifType = notification.type || (notification.data as any)?.type || '';
  const config = NOTIFICATION_CONFIG[notifType] || {
    label: 'Notification',
    icon: '🔔',
    accent: 'accent',
  };

  const getColorVars = (accentType: 'ok' | 'accent' | 'warn' | 'bad') => {
    const mapping = {
      ok: { accent: 'var(--ev-ok)', soft: 'var(--ev-ok-soft)' },
      accent: { accent: 'var(--ev-accent)', soft: 'var(--ev-accent-soft)' },
      warn: { accent: 'var(--ev-warn)', soft: 'var(--ev-warn-soft)' },
      bad: { accent: 'var(--ev-bad)', soft: 'var(--ev-bad-soft)' },
    };
    return mapping[accentType];
  };

  const colors = getColorVars(config.accent);

  const handleViewAction = () => {
    if (notification.url) {
      window.location.href = notification.url;
    }
  };

  const formattedTime = notification.ts
    ? new Date(notification.ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    : undefined;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center sm:p-4"
        style={{ background: 'rgba(0,0,0,.5)' }}
        onClick={onClose}
      >
        <div
          className="nudge-detail w-full sm:max-w-md"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'var(--ev-panel)',
            color: 'var(--ev-text)',
            borderRadius: '20px 20px 0 0',
            boxShadow: 'var(--ev-shadow, 0 24px 60px -20px rgba(0,0,0,.4))',
            padding: '22px 20px 20px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {/* Icône + titre + sous-titre */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 10 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: colors.soft,
                color: colors.accent,
                display: 'grid',
                placeItems: 'center',
                fontSize: 28,
              }}
            >
              {config.icon}
            </div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>
                {config.label}
              </div>
              {notification.title && (
                <div style={{ fontSize: 13, color: 'var(--ev-muted)', marginTop: 2 }}>
                  {notification.title}
                </div>
              )}
            </div>
          </div>

          {/* Corps du message */}
          {notification.body && (
            <div
              style={{
                marginTop: 18,
                borderRadius: 14,
                background: 'var(--ev-raised)',
                boxShadow: 'inset 0 0 0 1px var(--ev-line)',
                padding: '12px 14px',
              }}
            >
              <p
                style={{
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  color: 'var(--ev-text)',
                  margin: 0,
                }}
              >
                {notification.body}
              </p>
            </div>
          )}

          {/* Timestamp optionnel */}
          {formattedTime && (
            <p
              style={{
                fontSize: 12,
                color: 'var(--ev-faint)',
                marginTop: 14,
                textAlign: 'center',
              }}
            >
              {formattedTime}
            </p>
          )}

          {/* Actions */}
          <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {notification.url && (
              <button
                type="button"
                onClick={handleViewAction}
                style={{
                  height: 46,
                  borderRadius: 12,
                  background: colors.accent,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  fontWeight: 600,
                  fontSize: 14.5,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Consulter
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                height: 40,
                borderRadius: 12,
                background: 'transparent',
                color: 'var(--ev-muted)',
                border: 'none',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default NotificationDetailModal;
